import { Amount } from 'uom';
import { isDash } from '../core-utils/validators';
import { formatNumber, isMasked } from '../misc/sanitizers';
import { DASH, defaultTxt, EMPTY_STRING, TWO_DECIMALS_FORMAT } from '../misc/strings.constants';
import { symbolRetriever } from './currency/symbol';
import { areaUomAbbrev } from './uom-abbreviation';
import { digger } from '@cbre/digger';

/**
 * Returns '4 SF' or '$4 USD Dollar` if prefix `$` provided
 */
export const formatUomValues = (value, uom, prefix = '', suffix = '') =>
  `${prefix}${value != null ? value : DASH} ${uom != null ? uom : DASH}${suffix}`;

export interface UomFormat {
  /**
   * Specify the path where the value should be
   * value has precedence
   */
  valuePath: string;
  /**
   * Specify the path where the uom value should be
   * uom property has precedence
   */
  uomPath: string;
  /**
   * The value to be formatted
   */
  value: any;
  /**
   * The uom value
   */
  uom: string;
  prefix: string;
  suffix: string;
  defaultTxt: string;
  /**
   * Used by angular formatNumber function to return the number value formatted
   * by locale
   */
  location: string;
  digits: string;
}

const defaultUomFormat: UomFormat = {
  valuePath: '',
  uomPath: '',
  value: null,
  uom: '',
  prefix: '',
  suffix: '',
  defaultTxt: DASH,
  location: 'en-US',
  digits: '.0-2',
};

/**
 * Extract field value and field uom and return a formatUomValues response
 */
export const formatValueWithUom = (formatConfig: Partial<UomFormat>, data?: { [key: string]: any }) => {
  const options = { ...defaultUomFormat, ...formatConfig };
  const value = options.value || digger(data, options.valuePath);
  const uom = options.uom || digger(data, options.uomPath);
  return value != null
    ? formatUomValues(
        formatNumber(value, options.location, options.digits),
        areaUomAbbrev(uom),
        options.prefix,
        options.suffix
      )
    : options.defaultTxt;
};

const defaultCurrencyFormat: UomFormat = {
  valuePath: '',
  uomPath: '',
  value: null,
  uom: '',
  prefix: '$',
  suffix: '',
  defaultTxt: DASH,
  location: 'en-US',
  digits: TWO_DECIMALS_FORMAT,
};

/**
 * Formats currency fields (fields with uom holding rates/prices etc)
 * in the next pattern `$#,###.##`
 * @param formatConfig
 * @param data
 * @returns formatted currency field
 */
export const formatUomCurrency = (formatConfig: Partial<UomFormat>, data?: { [key: string]: any }): string => {
  const options = { ...defaultCurrencyFormat, ...formatConfig };
  const value = options.value || digger(data, options.valuePath);
  if (isMasked(value)) {
    return value;
  }
  const uom = options.uom || digger(data, options.uomPath);
  // Blank rates need to show ‘-' instead of '0’
  if ((value == null || value === EMPTY_STRING) && (uom == null || uom === EMPTY_STRING)) {
    return DASH;
  }
  const prefix = symbolRetriever(uom);
  return value != null && value !== EMPTY_STRING
    ? formatUomValues(
        formatNumber(value, options.location, options.digits || TWO_DECIMALS_FORMAT),
        EMPTY_STRING,
        prefix,
        options.suffix
      )
    : formatUomValues(options.defaultTxt, EMPTY_STRING, prefix, options.suffix);
};

/**
 * Extract field value and field uom and return a formatUomValues response with only the prefix UOM format
 */
export const formatValueWithPrefixedUom = (formatConfig: Partial<UomFormat>, data?: { [key: string]: any }) => {
  const options = { ...defaultUomFormat, ...formatConfig };
  const value = options.value || digger(data, options.valuePath);
  const uom = options.uom || digger(data, options.uomPath);
  const currencyPrefix = symbolRetriever(uom);
  return value != null
    ? formatUomValues(
        formatNumber(value, options.location, options.digits),
        '',
        options.prefix ? options.prefix : currencyPrefix,
        options.suffix
      )
    : options.defaultTxt;
};

/**
 * It uses `formatNumber` a number and `areaUomAbbrev` to return `<number> <uom>`
 * @see Amount.Amount
 *
 * @param amount `Amount.Amount` from 'uom'
 * @param locale string default `'en-US'`
 * @param digits string default `'.0-0'`
 */
export const formatAmountObject = (amount: Amount.Amount<any>, locale = 'en-US', digits = '.0-0') =>
  `${formatNumber(amount.value, locale, digits)} ${areaUomAbbrev(amount.unit.name)}`;

/**
 * It format two rate fields in the way of `min - max / suffix` and adds a suffix for both fields,
 * in case any (min or max) is a `-` then returns only one dash
 *
 * @param data {{ [key: string]: any }}
 * @param minUomField {Partial<UomFormat>}
 * @param maxUomField {Partial<UomFormat>}
 * @param separator string default `-`
 * @param suffix {'Yr' | 'Mo'}
 *
 * @returns string with following format `$min - $max / <Yr | Mo>`  OR `-`
 */
const formatMinMaxUomValues = (
  data: { [key: string]: any },
  minUomField: Partial<UomFormat>,
  maxUomField: Partial<UomFormat>,
  { separator = DASH, suffix }: { separator?: string; suffix: string }
) => {
  const formattedMinYear = formatUomCurrency(minUomField, data);
  const formattedMaxYear = formatUomCurrency(maxUomField, data);
  return !(isDash(formattedMinYear) && isDash(formattedMaxYear))
    ? `${formattedMinYear} ${separator} ${formattedMaxYear}/${suffix}`
    : defaultTxt;
};
